import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Calendar, Sparkles, Check, ChevronRight, Upload, Loader2, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two-step Book Now picker:
 *   Step 1 → choose between an existing service OR a custom project
 *   Step 2a → existing service → pick tier (basic / standard / premium) → /booking/<id>?tier=...
 *   Step 2b → custom project → fill brief + upload photos → POST to inquiries
 *
 * Props: { vendor, onClose }
 */
export default function BookNowPicker({ vendor, onClose }) {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [step, setStep] = useState('chooser'); // chooser | tier | custom
  const [selectedService, setSelectedService] = useState(null);

  const services = vendor?.services?.filter((s) => s.is_active !== false) || [];

  const goToBooking = (service, tier) => {
    const tierQs = tier ? `?tier=${tier.name || tier}` : '';
    const target = `/booking/${service.service_id}${tierQs}`;
    onClose?.();
    if (!user) {
      try { sessionStorage.setItem('postAuthRedirect', target); } catch {}
      toast.info('Create a free account to book — takes 30 seconds.');
      navigate(`/auth?mode=register&return=${encodeURIComponent(target)}`);
      return;
    }
    navigate(target);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="book-now-modal">
      <div className="bg-[#0E0E23] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-white font-syne font-bold text-lg">
              {step === 'chooser' && 'Book with ' + (vendor?.business_name || vendor?.owner_name)}
              {step === 'tier' && `Pick your tier · ${selectedService?.title}`}
              {step === 'custom' && 'Tell us about your custom project'}
            </h2>
            <p className="text-white/85 text-xs mt-0.5">
              {step === 'chooser' && 'Choose an existing service or describe a custom project'}
              {step === 'tier' && 'Three pricing options to fit your budget'}
              {step === 'custom' && 'Goes straight to the vendor\'s inquiries inbox'}
            </p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1" data-testid="book-modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-3">
          {/* ─────── STEP 1: Chooser ─────── */}
          {step === 'chooser' && (
            <>
              {services.length === 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-amber-200 text-sm">
                  This vendor hasn't listed any services yet — but you can still send them a custom project request below.
                </div>
              )}

              {services.length > 0 && (
                <div className="space-y-2" data-testid="book-existing-services">
                  <p className="text-[#A78BFA] text-xs font-bold uppercase tracking-wider">Existing services</p>
                  {services.map((s) => {
                    const fromPrice = Math.min(...((s.packages || []).map((p) => p.price).filter((p) => p)));
                    return (
                      <button
                        key={s.service_id}
                        type="button"
                        onClick={() => { setSelectedService(s); setStep('tier'); }}
                        className="w-full text-left bg-[#1E1E3A]/70 hover:bg-[#1E1E3A] border border-white/10 hover:border-[#8B5CF6]/50 rounded-xl p-4 transition-all flex items-center gap-3 group"
                        data-testid={`book-service-${s.service_id}`}
                      >
                        {s.images?.[0] ? (
                          <img src={s.images[0].startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${s.images[0]}` : s.images[0]} alt="" className="w-14 h-14 rounded-lg object-cover" />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#8B5CF6]/20 to-[#3B82F6]/10 flex items-center justify-center">
                            <ImageIcon size={20} className="text-[#A78BFA]" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold truncate">{s.title}</p>
                          <p className="text-[#9CA3AF] text-xs truncate">{s.description}</p>
                          {Number.isFinite(fromPrice) && (
                            <p className="text-[#FCD34D] text-xs font-mono mt-1">From ${fromPrice}</p>
                          )}
                        </div>
                        <ChevronRight size={18} className="text-[#9CA3AF] group-hover:text-white transition-colors" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Custom project option */}
              <div className="pt-2 mt-3 border-t border-white/5">
                <p className="text-[#FCD34D] text-xs font-bold uppercase tracking-wider mb-2">Or…</p>
                <button
                  type="button"
                  onClick={() => {
                    if (!user) {
                      try { sessionStorage.setItem('postAuthRedirect', `/vendor/${vendor?.vendor_id || ''}`); } catch {}
                      toast.info('Create a free account to send a custom request.');
                      onClose?.();
                      navigate(`/auth?mode=register&return=${encodeURIComponent(`/vendor/${vendor?.vendor_id || ''}`)}`);
                      return;
                    }
                    setStep('custom');
                  }}
                  className="w-full text-left bg-gradient-to-br from-amber-500/10 to-[#8B5CF6]/10 hover:from-amber-500/15 hover:to-[#8B5CF6]/15 border border-amber-500/30 rounded-xl p-4 transition-all flex items-center gap-3 group"
                  data-testid="book-custom-project-btn"
                >
                  <div className="w-14 h-14 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Sparkles size={22} className="text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-bold">Custom project</p>
                    <p className="text-amber-200 text-xs mt-0.5">Don't see what you need? Describe your project + send photos.</p>
                  </div>
                  <ChevronRight size={18} className="text-amber-400 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </>
          )}

          {/* ─────── STEP 2a: Tier picker ─────── */}
          {step === 'tier' && selectedService && (
            <TierPicker
              service={selectedService}
              onPick={(tier) => goToBooking(selectedService, tier)}
              onCustom={() => setStep('custom')}
              onBack={() => { setSelectedService(null); setStep('chooser'); }}
            />
          )}

          {/* ─────── STEP 2b: Custom project form ─────── */}
          {step === 'custom' && (
            <CustomProjectForm
              vendor={vendor}
              user={user}
              authHeaders={getAuthHeaders}
              onBack={() => setStep('chooser')}
              onSubmitted={() => {
                onClose?.();
                toast.success('Custom project sent — vendor will reply via your messages.');
              }}
              prefilledService={selectedService}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────── Tier picker (3 packages) ───────────
function TierPicker({ service, onPick, onCustom, onBack }) {
  const packages = service.packages || [];
  return (
    <>
      <button onClick={onBack} className="text-[#A78BFA] hover:text-white text-xs flex items-center gap-1 mb-3" data-testid="tier-back">
        <ChevronRight size={14} className="rotate-180" /> Back to services
      </button>
      <div className="space-y-2" data-testid="tier-list">
        {packages.map((p) => {
          const isPriority = p.name === 'standard';
          return (
            <button
              key={p.name}
              type="button"
              onClick={() => onPick(p)}
              className={`w-full text-left rounded-xl p-4 transition-all flex items-start gap-3 group ${
                isPriority
                  ? 'bg-gradient-to-br from-[#8B5CF6]/20 to-[#3B82F6]/15 border-2 border-[#8B5CF6]/60'
                  : 'bg-[#1E1E3A]/70 hover:bg-[#1E1E3A] border border-white/10 hover:border-[#8B5CF6]/50'
              }`}
              data-testid={`tier-${p.name}`}
            >
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-white font-bold capitalize flex items-center gap-2">
                    {p.title || p.name}
                    {isPriority && <span className="text-[10px] font-black bg-amber-400 text-black px-1.5 py-0.5 rounded">RECOMMENDED</span>}
                  </p>
                  <p className="text-[#FCD34D] font-mono font-bold text-lg">${p.price}</p>
                </div>
                <p className="text-[#9CA3AF] text-xs mt-1">{p.description}</p>
                {p.features?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {p.features.slice(0, 4).map((f, i) => (
                      <li key={i} className="text-[#C4B5FD] text-xs flex items-start gap-1.5">
                        <Check size={12} className="text-emerald-400 flex-shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                )}
                {p.delivery_days && (
                  <p className="text-[#64748B] text-[10px] mt-2 uppercase tracking-wider">{p.delivery_days}-day turnaround</p>
                )}
              </div>
            </button>
          );
        })}
      </div>
      <button
        onClick={onCustom}
        className="w-full mt-4 px-4 py-2.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/30 text-amber-300 text-sm font-semibold flex items-center justify-center gap-2"
        data-testid="tier-go-custom"
      >
        <Sparkles size={14} /> Need something different? Send a custom project request
      </button>
    </>
  );
}

// ─────────── Custom project form (with photo uploads) ───────────
function CustomProjectForm({ vendor, user, authHeaders, onBack, onSubmitted, prefilledService }) {
  const [title, setTitle] = useState(prefilledService ? `Custom: ${prefilledService.title}` : '');
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  const [deadline, setDeadline] = useState('');
  const [photos, setPhotos] = useState([]); // {file, preview}
  const [submitting, setSubmitting] = useState(false);

  const onPickPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 6 - photos.length;
    const accepted = files.slice(0, remaining).map((f) => {
      const isPdf = (f.type === 'application/pdf') || /\.pdf$/i.test(f.name);
      return {
        file: f,
        preview: isPdf ? null : URL.createObjectURL(f),
        is_pdf: isPdf,
        name: f.name,
      };
    });
    setPhotos((prev) => [...prev, ...accepted]);
    e.target.value = '';
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!user) return toast.error('Please sign in first');
    if (!title.trim() || !description.trim()) return toast.error('Title and description required');
    setSubmitting(true);
    try {
      // 1) Upload photos (if any) to media_uploads via existing service-image upload? Use a generic media upload
      const uploadedUrls = [];
      for (const p of photos) {
        const fd = new FormData();
        fd.append('file', p.file);
        try {
          const r = await fetch(`${API}/media/upload`, { method: 'POST', headers: authHeaders(), body: fd });
          const d = await r.json();
          if (r.ok && (d.url || d.file_url)) uploadedUrls.push(d.url || d.file_url);
        } catch { /* swallow per-photo */ }
      }
      // 2) POST inquiry / custom request
      const payload = {
        vendor_id: vendor.vendor_id,
        title: title.trim(),
        description: description.trim(),
        budget: budget.trim() || null,
        deadline: deadline || null,
        attachment_urls: uploadedUrls,
        type: 'custom_project',
        based_on_service_id: prefilledService?.service_id || null,
      };
      const r = await fetch(`${API}/messages/inquiries/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Failed to send');
      onSubmitted?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="custom-project-form">
      <button onClick={onBack} className="text-[#A78BFA] hover:text-white text-xs flex items-center gap-1 mb-2" data-testid="custom-back">
        <ChevronRight size={14} className="rotate-180" /> Back
      </button>

      <div>
        <Label className="text-[#9CA3AF] text-xs">Project title *</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Custom turquoise wedding ring"
          className="bg-white/5 border-white/10 text-white rounded-xl h-11 mt-1"
          data-testid="custom-title"
          maxLength={120}
        />
      </div>

      <div>
        <Label className="text-[#9CA3AF] text-xs">Describe what you need *</Label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Materials, dimensions, style, deadline notes, where it's going, etc."
          className="bg-white/5 border-white/10 text-white rounded-xl min-h-[120px] mt-1 placeholder:text-white/25"
          data-testid="custom-description"
          maxLength={2000}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[#9CA3AF] text-xs">Budget (optional)</Label>
          <Input
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="$500-$1000"
            className="bg-white/5 border-white/10 text-white rounded-xl h-11 mt-1"
            data-testid="custom-budget"
            maxLength={60}
          />
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs">Need by (optional)</Label>
          <Input
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="bg-white/5 border-white/10 text-white rounded-xl h-11 mt-1"
            data-testid="custom-deadline"
          />
        </div>
      </div>

      {/* Photo uploads */}
      <div>
        <Label className="text-[#9CA3AF] text-xs">Reference photos (up to 6)</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {photos.map((p, i) => (
            <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-white/10 group bg-fuchsia-500/10">
              {p.is_pdf ? (
                <div className="w-full h-full flex flex-col items-center justify-center p-1 text-center">
                  <span className="text-[20px] font-black text-fuchsia-300 leading-none">PDF</span>
                  <span className="text-[8px] text-zinc-400 mt-1 truncate w-full px-0.5">{p.name}</span>
                </div>
              ) : (
                <img src={p.preview} alt={`Reference ${i + 1}`} className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={() => removePhoto(i)}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`custom-remove-photo-${i}`}
              >
                <X size={10} />
              </button>
            </div>
          ))}
          {photos.length < 6 && (
            <label className="w-20 h-20 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-[#8B5CF6]/50 transition-colors" data-testid="custom-add-photo">
              <Upload size={16} className="text-[#9CA3AF] mb-1" />
              <span className="text-[10px] text-[#9CA3AF]">{photos.length}/6</span>
              <span className="text-[8px] text-[#64748B]">JPG · PDF</span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onPickPhotos} multiple />
            </label>
          )}
        </div>
      </div>

      <button
        onClick={submit}
        disabled={submitting}
        className="w-full py-3 rounded-xl font-bold text-black bg-gradient-to-r from-[#FCD34D] to-[#F59E0B] disabled:opacity-50 transition-all flex items-center justify-center gap-2 mt-2"
        data-testid="custom-submit"
      >
        {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Calendar size={14} /> Send custom project request</>}
      </button>
    </div>
  );
}
